import Link from "next/link";
import { useContext, useState } from "react";
import { useSelector } from "react-redux";

import { InternalButton } from "@sharedComponents/buttons";
import ProductInfo from "@sharedComponents/cards/product/info";

import SaveDummyShopPrompt from "@components/modals/save-shop-prompt";
import { useStoreContext } from "@components/store/provider";
import { ProductListingContext } from "@components/store/products/listing/listing-provider";

import { useCreateShopContext } from "@contexts/create-shop-context";
import { useCreateSwagContext } from "@contexts/create-swag-context";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import useProductDetailsModal from "@hooks/product-detail/use-product-details-modal";
import usePopulateAutomatedCatalog from "@hooks/automated-catalog/use-populate-automated-catalog";
import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";

import { getIsLockerShop, getIsPricelessShop } from "@selectors/store";
import { getReceivedItemsCount } from "@selectors/cart";
import { selectFilterActiveColorsCount } from "@stadium/redux-utils/selectors/filterSelector";

import { getProductRoute } from "@constants/routes";
import { ANALYTICS_TARGET_PRODUCT } from "@stadium/services/analytics/constants/default";

import styles from "./product-tile.module.scss";

const ProductBlock = (props) => {
  const {
    data,
    className = styles.productBlock,
    openProductDetailsModal,
    productLink,
    target = "",
    eventLocationAnalytics,
  } = props;

  const { id, slug, broken = false, brokenVariantId = "", colorOptions, updatedAt } = data;

  const { isDesktopDevice } = useContext(CurrentDeviceContext);
  const [isSaveDummyShopModalOpen, setIsSaveDummyShopModalOpen] = useState(false);
  const { selectedColorOptionTypes } = useContext(ProductListingContext);
  const activeColorsCount = useSelector(selectFilterActiveColorsCount);

  const { isNewShopCreationFlow } = useCreateShopContext();

  const closeSaveDummyShopModal = () => setIsSaveDummyShopModalOpen(false);
  const openSaveDummyShopModal = () => setIsSaveDummyShopModalOpen(true);

  const isLockerShop = useSelector(getIsLockerShop);
  const isPricelessShop = useSelector(getIsPricelessShop);
  const isRLPFlow = useSelector(getReceivedItemsCount);
  const { isSwagCreationShopFlow, draftCatalogSelectedProductsPresence } = useCreateSwagContext();
  const {
    addProductToAutomatedCatalog,
    removeProductFromAutomatedCatalog,
    loading: automatedCatalogLoading,
  } = usePopulateAutomatedCatalog();

  const { storeBasePath, isPreviewCatalogMode, isNewSwagmagicHost } = useStoreContext();

  const { openProductDetailsModalCatalog, renderProductDetailsModalCatalog } =
    useProductDetailsModal();

  const withAnalytics = useWithAnalytics();

  const activeColorOptionTypesLength = isNewSwagmagicHost
    ? activeColorsCount
    : selectedColorOptionTypes?.length || 0;

  const isRenderProductDetailModal =
    isPreviewCatalogMode || (isPricelessShop && !isRLPFlow) || isSwagCreationShopFlow;

  const productRoute = getProductRoute({
    productSlug: slug,
    storeBasePath,
    broken,
    brokenVariantId,
  });

  const modifiedEventLocationAnalytics = {
    target: ANALYTICS_TARGET_PRODUCT,
    ...eventLocationAnalytics,
  };

  const handleProductCardClick = (e) => {
    withAnalytics(undefined, modifiedEventLocationAnalytics)();
    if (openProductDetailsModal) {
      e.preventDefault();
      openProductDetailsModal(id);
    }
    if (isPricelessShop && !isRLPFlow) {
      e.preventDefault();
      openProductDetailsModalCatalog();
    }
    // need to pass this in rsc cache call - do not remove
    globalThis.__p_key = updatedAt ? Number(new Date(updatedAt)) : 0;
  };

  const handleProductCardButtonClick = () => {
    if (isNewShopCreationFlow) {
      openSaveDummyShopModal();
    } else {
      openProductDetailsModalCatalog();
    }
  };

  const numberOfColorDots = activeColorOptionTypesLength > 0 ? 0 : 1;
  const showColorDots = colorOptions?.length > numberOfColorDots;

  const isInCollection = draftCatalogSelectedProductsPresence?.[String(data.productId)];

  const handleAddRemoveToCollection = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (automatedCatalogLoading) return;

    const productId = Number(data.productId);

    if (isInCollection) {
      await removeProductFromAutomatedCatalog({ productId });
      return;
    }

    await addProductToAutomatedCatalog({ productId });
  };

  const renderProductInfo = () => (
    <ProductInfo
      {...props}
      isLockerShop={isLockerShop}
      isPricelessShop={isPricelessShop}
      showColorDots={showColorDots}
      isSwagCreationShopFlow={isSwagCreationShopFlow}
      isInCollection={isInCollection}
      automatedCatalogLoading={automatedCatalogLoading}
      handleAddRemoveToCollection={handleAddRemoveToCollection}
      isNewSwagmagicHost={isNewSwagmagicHost}
      isResponsiveView={!isDesktopDevice}
    />
  );

  const card = () => {
    return (
      <>
        {!isPreviewCatalogMode && !isNewShopCreationFlow && !isSwagCreationShopFlow ? (
          <Link
            href={productLink || productRoute}
            onClick={handleProductCardClick}
            prefetch={false}
            target={target}
          >
            {renderProductInfo()}
          </Link>
        ) : (
          <InternalButton
            styleType="no-style"
            className={styles.productCatalogCardHolder}
            key={id}
            onClick={handleProductCardButtonClick}
            displayChildren={false}
            title={data?.name}
            eventLocationAnalytics={modifiedEventLocationAnalytics}
          >
            {renderProductInfo()}
          </InternalButton>
        )}
        {isRenderProductDetailModal && renderProductDetailsModalCatalog({ data })}
      </>
    );
  };

  return (
    <div className={className}>
      {card()}
      {isSaveDummyShopModalOpen && <SaveDummyShopPrompt closeModal={closeSaveDummyShopModal} />}
    </div>
  );
};

export default ProductBlock;
